import { FC } from "react";
import {
  NodeProperties,
  ConnectedEntity,
  NodeType,
  RelationshipType
} from "modules/knowledge-library/data/models/knowledge-graph.model";

type PropertiesPanelProps = {
  properties: NodeProperties;
  connectedEntities: ConnectedEntity[];
};

export const getEntityIconColor = (type: NodeType): string => {
  switch (type) {
    case NodeType.ASSET:
      return "#5b21b6";
    case NodeType.SYSTEM:
      return "#2e7d32";
    case NodeType.SUBSYSTEM:
      return "#d97706";
    case NodeType.EQUIPMENT:
      return "#2563eb";
    case NodeType.REQUIREMENT:
      return "#dc2626";
    case NodeType.DISCIPLINE:
      return "#d97706";
    case NodeType.DOCUMENT:
      return "#2563eb";
    case NodeType.COMPONENT:
      return "#d97706";
    case NodeType.SENSOR:
      return "#dc2626";
    default:
      return "#6b7280";
  }
};

export const getEntityBgColor = (type: NodeType): string => {
  switch (type) {
    case NodeType.ASSET:
      return "#ede9fe";
    case NodeType.SYSTEM:
      return "#e8f5e9";
    case NodeType.SUBSYSTEM:
      return "#fef9e7";
    case NodeType.EQUIPMENT:
      return "#e3f0ff";
    case NodeType.REQUIREMENT:
      return "#fde8e8";
    case NodeType.DISCIPLINE:
      return "#fef9e7";
    case NodeType.DOCUMENT:
      return "#e3f0ff";
    case NodeType.COMPONENT:
      return "#fef9e7";
    case NodeType.SENSOR:
      return "#fde8e8";
    default:
      return "#f3f4f6";
  }
};

const getRelationshipLabel = (rel: RelationshipType): string => {
  switch (rel) {
    case RelationshipType.CONTAINS:
      return "CONTAINS";
    case RelationshipType.OWNED_BY:
      return "OWNED_BY";
    case RelationshipType.HAS:
      return "HAS";
    case RelationshipType.CONSTRAINT:
      return "CONSTRAINT";
    case RelationshipType.HAS_COMPONENT:
      return "HAS_COMPONENT";
    case RelationshipType.HAS_REQUIREMENT:
      return "HAS_REQUIREMENT";
    case RelationshipType.SOURCED_FROM:
      return "SOURCED_FROM";
    case RelationshipType.MONITORED_BY:
      return "MONITORED_BY";
    default:
      return rel;
  }
};

const PropertiesPanel: FC<PropertiesPanelProps> = ({
  properties,
  connectedEntities
}) => {
  return (
    <div className="properties-panel">
      {/* <h2 className="properties-panel__heading">Place Holders</h2> */}
      <h3 className="properties-panel__title">PROPERTIES</h3>

      <div className="properties-panel__list">
        {Object.entries(properties).map(([key, value]) => (
          <div className="properties-panel__row" key={key}>
            <span className="properties-panel__label">
              {key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (s) => s.toUpperCase())}
            </span>
            {key === "status" ? (
              <span className="properties-panel__value properties-panel__value--status">
                <span className="properties-panel__status-dot" />
                {value}
              </span>
            ) : (
              <span className="properties-panel__value">{value}</span>
            )}
          </div>
        ))}
      </div>

      <h3 className="properties-panel__title properties-panel__title--entities">
        CONNECTED ENTITIES ({connectedEntities.length})
      </h3>

      <div className="properties-panel__entities">
        {connectedEntities.map((entity) => (
          <div key={entity.id} className="properties-panel__entity">
            <div
              className="properties-panel__entity-icon"
              style={{
                backgroundColor: getEntityBgColor(entity.type),
                color: getEntityIconColor(entity.type)
              }}
            >
              <EntityIcon type={entity.type} />
            </div>
            <div className="properties-panel__entity-info">
              <span className="properties-panel__entity-name">
                {entity.name}
              </span>
              <span className="properties-panel__entity-rel">
                {entity.category}
                {/* &bull;{" "}
                {getRelationshipLabel(entity.relationship)} */}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const EntityIcon: FC<{ type: NodeType }> = ({ type }) => {
  const color = getEntityIconColor(type);
  switch (type) {
    case NodeType.ASSET:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="6"
            width="18"
            height="12"
            rx="2"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="3"
            y1="10"
            x2="21"
            y2="10"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="12"
            y1="10"
            x2="12"
            y2="18"
            stroke={color}
            strokeWidth="1.2"
          />
        </svg>
      );
    case NodeType.SYSTEM:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="12" r="3" fill={color} />
          <line
            x1="12"
            y1="4"
            x2="12"
            y2="7"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="12"
            y1="17"
            x2="12"
            y2="20"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="4"
            y1="12"
            x2="7"
            y2="12"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="17"
            y1="12"
            x2="20"
            y2="12"
            stroke={color}
            strokeWidth="1.5"
          />
        </svg>
      );
    case NodeType.SUBSYSTEM:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="4"
            y="4"
            width="16"
            height="16"
            rx="3"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="4"
            y1="12"
            x2="20"
            y2="12"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="12"
            y1="4"
            x2="12"
            y2="20"
            stroke={color}
            strokeWidth="1.2"
          />
        </svg>
      );
    case NodeType.EQUIPMENT:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.2" />
          <line
            x1="12"
            y1="5"
            x2="12"
            y2="9"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="12"
            y1="15"
            x2="12"
            y2="19"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="5"
            y1="12"
            x2="9"
            y2="12"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="15"
            y1="12"
            x2="19"
            y2="12"
            stroke={color}
            strokeWidth="1.2"
          />
        </svg>
      );
    case NodeType.DISCIPLINE:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M 12 4 L 18 9 L 16 18 L 8 18 L 6 9 Z"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
          <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.2" />
        </svg>
      );
    case NodeType.SENSOR:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.5" />
          <path
            d="M8 12 Q10 8 12 12 Q14 16 16 12"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      );
    case NodeType.REQUIREMENT:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="4"
            y="4"
            width="16"
            height="16"
            rx="2"
            stroke={color}
            strokeWidth="1.5"
          />
          <rect
            x="8"
            y="8"
            width="8"
            height="8"
            rx="1"
            stroke={color}
            strokeWidth="1.2"
          />
        </svg>
      );
    case NodeType.DOCUMENT:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="5"
            y="3"
            width="14"
            height="18"
            rx="2"
            stroke={color}
            strokeWidth="1.5"
          />
          <line x1="8" y1="8" x2="16" y2="8" stroke={color} strokeWidth="1.2" />
          <line
            x1="8"
            y1="12"
            x2="16"
            y2="12"
            stroke={color}
            strokeWidth="1.2"
          />
          <line
            x1="8"
            y1="16"
            x2="13"
            y2="16"
            stroke={color}
            strokeWidth="1.2"
          />
        </svg>
      );
    case NodeType.COMPONENT:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="2"
            stroke={color}
            strokeWidth="1.5"
          />
          <circle cx="9" cy="12" r="3" stroke={color} strokeWidth="1.2" />
          <circle cx="16" cy="12" r="2" stroke={color} strokeWidth="1.2" />
        </svg>
      );
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="12" r="3" fill={color} />
        </svg>
      );
  }
};

export default PropertiesPanel;
